/**
 * /app/salon — Dine-In: mapa del salón con estado de mesas en tiempo real.
 * Muestra pedidos por atender (comanda sin imprimir / QR mesa).
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useCallback, useMemo } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { SalonTour } from "@/components/ProductTour";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { UtensilsCrossed, RefreshCw, Users, QrCode, Bell, Eye, Settings2, CalendarCheck } from "lucide-react";
import { CrudEmptyState, FilterBar, CrudShellHeader, Loading } from "@/components/ds";
import { SalonFloorSettings } from "@/components/salon/SalonFloorSettings";
import {
  SalonReservationSheet,
  type SalonReservation,
} from "@/components/salon/SalonReservationSheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { localCalendarDateIso } from "@/lib/format-date";
import { useOperationalMode, type OperationalShortcut } from "@/hooks/use-operational-mode";
import { OperationalModeShell, OperationalModeToggle } from "@/components/operational-mode/OperationalModeShell";
import { OPERATIONAL_HOST_LAYOUT } from "@/lib/operational-mode-classes";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";

export const Route = createFileRoute("/app/salon/")({
  component: SalonPage,
  validateSearch: (s: Record<string, unknown>) => ({
    config: s.config === true || s.config === "1" || s.config === 1 ? true : undefined,
  }),
  head: () => ({ meta: [{ title: "Salón — Dine-In" }] }),
});

type TableStatus = "LIBRE" | "OCUPADA" | "PIDIENDO" | "PAGO_PENDIENTE";

interface TableAttention {
  sessionId: string;
  totalAcumulado: number;
  comensales: number;
  orderCount: number;
  pendingKitchen: number;
  qrOrderCount: number;
  needsAttention: boolean;
}

interface DineTable {
  id: string;
  nombre: string;
  capacidad: number;
  estado: TableStatus;
  qrToken: string;
  sectorId: string | null;
  attention?: TableAttention | null;
}

interface Sector {
  id: string;
  nombre: string;
}

type DayReservation = SalonReservation;

const STATUS_CONFIG: Record<TableStatus, { label: string; color: string; bg: string }> = {
  LIBRE: { label: "Libre", color: "text-green-400", bg: "bg-green-500/10 border-green-500/30" },
  OCUPADA: { label: "Ocupada", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30" },
  PIDIENDO: { label: "Pidiendo", color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/30" },
  PAGO_PENDIENTE: { label: "Pago pendiente", color: "text-red-400", bg: "bg-red-500/10 border-red-500/30" },
};

const pesos = (n: number) =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(n);

function TableCard({
  table,
  tid,
  reservation,
  expanded,
  onOpen,
  onClose,
  onSplit,
  onReservationClick,
}: {
  table: DineTable;
  tid: string | null;
  reservation?: DayReservation | null;
  /** true cuando el Salón está en modo operativo expandido — amplía touch targets y oculta acciones secundarias */
  expanded?: boolean;
  onOpen: (tableId: string) => void;
  onClose: (tableId: string) => void;
  onSplit: (tableId: string) => void;
  onReservationClick?: (reservation: DayReservation) => void;
}) {
  const cfg = STATUS_CONFIG[table.estado];
  const isActive = table.estado !== "LIBRE";
  const att = table.attention;
  const needsAttention = Boolean(att?.needsAttention);
  const pending = att?.pendingKitchen ?? 0;
  const qrCount = att?.qrOrderCount ?? 0;

  return (
    <div
      className={cn(
        "flex flex-col rounded-2xl border p-3.5 transition-colors sm:p-4",
        needsAttention
          ? "border-amber-500/50 bg-amber-500/10 shadow-[0_0_0_1px_rgba(245,158,11,0.15)]"
          : reservation && table.estado === "LIBRE"
            ? "border-violet-500/40 bg-violet-500/10"
            : cfg.bg,
      )}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <span className="block text-base font-semibold leading-tight">{table.nombre}</span>
          <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
            <Users className="h-3.5 w-3.5" />
            {att?.comensales || table.capacidad} comensales
          </div>
        </div>
        <Badge variant="outline" className={cn("shrink-0 text-[10px]", cfg.bg, cfg.color)}>
          {cfg.label}
        </Badge>
      </div>

      {reservation && (reservation.status === "confirmed" || reservation.status === "seated") ? (
        <button
          type="button"
          className="mb-2.5 w-full rounded-lg border border-violet-500/30 bg-violet-500/15 px-2.5 py-2 text-left text-[11px] text-violet-100 transition-colors hover:border-violet-400/50 hover:bg-violet-500/25"
          onClick={(e) => {
            e.stopPropagation();
            onReservationClick?.(reservation);
          }}
        >
          <div className="flex items-center gap-1 font-semibold">
            <CalendarCheck className="h-3 w-3" />
            Reserva {reservation.reservedTime.slice(0, 5)}
            <span className="ml-auto text-[10px] font-normal text-violet-200/70">Ver · WA</span>
          </div>
          <p className="mt-0.5 text-violet-100/80">
            {reservation.guestName} · {reservation.partySize} pers.
            {reservation.serviceNombre ? ` · ${reservation.serviceNombre}` : ""}
          </p>
        </button>
      ) : null}

      {needsAttention && (
        <div className="mb-2.5 flex flex-wrap gap-1.5">
          {pending > 0 && (
            <span className="inline-flex items-center gap-1 rounded-lg bg-amber-500/20 px-2 py-1 text-[11px] font-semibold text-amber-200">
              <Bell className="h-3 w-3" />
              {pending} por imprimir
            </span>
          )}
          {qrCount > 0 && (
            <span className="inline-flex items-center gap-1 rounded-lg bg-sky-500/20 px-2 py-1 text-[11px] font-semibold text-sky-200">
              <QrCode className="h-3 w-3" />
              Pedido QR
            </span>
          )}
        </div>
      )}

      {isActive && att && att.totalAcumulado > 0 && (
        <p className="mb-2 text-sm font-bold tabular-nums text-primary">{pesos(att.totalAcumulado)}</p>
      )}

      <div className="mt-auto flex flex-col gap-2">
        {table.estado === "LIBRE" && (
          <Button
            className={cn("w-full text-sm", expanded ? "h-14 text-base" : "h-11")}
            onClick={() => onOpen(table.id)}
          >
            Abrir mesa
          </Button>
        )}
        {isActive && (
          <>
            <Button className={cn("w-full text-sm", expanded ? "h-14 text-base" : "h-11")} asChild>
              <Link to="/app/salon/comanda" search={{ tableId: table.id }}>
                {needsAttention ? (
                  <>
                    <Eye className="mr-1.5 h-4 w-4" />
                    Ver pedido
                  </>
                ) : (
                  "Pedido"
                )}
              </Link>
            </Button>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                className={cn("text-xs sm:text-sm", expanded ? "h-12" : "h-11")}
                title="Repartir el total entre comensales al cobrar"
                onClick={() => onSplit(table.id)}
              >
                Dividir
              </Button>
              <Button
                variant="ghost"
                className={cn("text-xs sm:text-sm", expanded ? "h-12" : "h-11")}
                onClick={() => onClose(table.id)}
              >
                Cerrar
              </Button>
            </div>
          </>
        )}
        {/* En modo expandido se oculta el QR — acción secundaria, reduce ruido táctil */}
        {!expanded && (
          <Button
            variant="ghost"
            className="h-10 w-full justify-start gap-2 px-2 text-xs text-muted-foreground"
            title="Imprimir QR del menú de mesa"
            onClick={() => {
              if (!tid) {
                toast.error("Sin tenant activo");
                return;
              }
              window.open(
                `/api/tables/qr-pdf?tableId=${encodeURIComponent(table.id)}&tid=${encodeURIComponent(tid)}`,
                "_blank",
              );
            }}
          >
            <QrCode className="h-3.5 w-3.5" />
            QR mesa
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * SCRUM-83: reemplaza el `window.prompt()` nativo que pedía la cantidad de
 * comensales al abrir una mesa — violaba la regla del proyecto de nunca usar
 * diálogos nativos, y además no era touch-friendly (numpad chico, sin
 * estilos, bloqueante).
 */
function OpenTableDialog({
  open,
  defaultComensales,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  defaultComensales: number;
  onOpenChange: (open: boolean) => void;
  onConfirm: (comensales: number) => void;
}) {
  const [value, setValue] = useState(String(defaultComensales));
  useEffect(() => {
    if (open) setValue(String(defaultComensales));
  }, [open, defaultComensales]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xs">
        <DialogHeader>
          <DialogTitle>Abrir mesa</DialogTitle>
          <DialogDescription>¿Cuántos comensales se sientan?</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-center gap-3 py-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-12 w-12 text-lg"
            onClick={() => setValue((v) => String(Math.max(1, (parseInt(v) || 1) - 1)))}
          >
            −
          </Button>
          <input
            type="number"
            min={1}
            inputMode="numeric"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="h-12 w-20 rounded-xl border border-border/60 bg-card text-center text-xl font-bold tabular-nums"
            autoFocus
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-12 w-12 text-lg"
            onClick={() => setValue((v) => String((parseInt(v) || 0) + 1))}
          >
            +
          </Button>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => {
              const n = parseInt(value) || 1;
              onOpenChange(false);
              onConfirm(n);
            }}
          >
            Abrir mesa
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SalonPage() {
  const tid = useWorkspaceTenantId();
  const navigate = useNavigate();
  const { config: configMode } = Route.useSearch();
  const [tables, setTables] = useState<DineTable[]>([]);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [dayReservations, setDayReservations] = useState<DayReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);
  const [activeSector, setActiveSector] = useState<string>("all");
  const [selectedReservation, setSelectedReservation] = useState<DayReservation | null>(null);
  const [openTarget, setOpenTarget] = useState<{ tableId: string; defaultComensales: number } | null>(null);

  const load = useCallback(async () => {
    if (!tid) return;
    try {
      const today = localCalendarDateIso();
      const [tr, sr, rr] = await Promise.all([
        apiGetJson(`/api/tables`, tid) as Promise<{ tables?: DineTable[]; _lock?: PlanApiErrorPayload }>,
        apiGetJson(`/api/tables?action=sectors`, tid) as Promise<{ sectors?: Sector[] }>,
        apiGetJson(`/api/reservations?date=${encodeURIComponent(today)}`, tid).catch(() => ({
          reservations: [] as DayReservation[],
        })) as Promise<{ reservations?: DayReservation[]; _lock?: PlanApiErrorPayload }>,
      ]);
      if (tr._lock) {
        setLock(tr._lock);
        return;
      }
      setTables(tr.tables ?? []);
      setSectors(sr.sectors ?? []);
      if (!rr._lock) {
        setDayReservations(
          (rr.reservations ?? []).filter(
            (r) => r.status === "confirmed" || r.status === "seated",
          ),
        );
      }
    } catch {
      toast.error("Error al cargar mesas");
    } finally {
      setLoading(false);
    }
  }, [tid]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-refresh liviano para captar pedidos QR
  useEffect(() => {
    if (!tid) return;
    const id = window.setInterval(() => {
      void load();
    }, 20000);
    return () => window.clearInterval(id);
  }, [tid, load]);

  const handleOpen = (tableId: string, presetParty?: number) => {
    setOpenTarget({ tableId, defaultComensales: presetParty && presetParty > 0 ? presetParty : 2 });
  };

  const confirmOpenTable = async (comensales: number) => {
    if (!tid || !openTarget) return;
    const r = (await apiSendJson(
      `/api/table-sessions`,
      "POST",
      {
        tableId: openTarget.tableId,
        comensales,
      },
      tid,
    )) as { session?: { id: string }; error?: string };
    if (r.error) {
      toast.error(r.error);
      return;
    }
    toast.success("Comanda abierta");
    load();
  };

  const handleClose = async (tableId: string) => {
    if (!tid) return;
    const r = (await apiGetJson(`/api/table-sessions?tableId=${tableId}`, tid)) as {
      session?: { id: string };
    };
    if (!r.session) {
      toast.error("No hay sesión abierta");
      return;
    }
    const closed = (await apiSendJson(
      `/api/table-sessions`,
      "POST",
      {
        action: "close",
        sessionId: r.session.id,
      },
      tid,
    )) as { session?: { status: string }; error?: string };
    if (closed.error) {
      toast.error(closed.error);
      return;
    }
    toast.success("Comanda cerrada");
    load();
  };

  const handleSplit = async (tableId: string) => {
    if (!tid) return;
    try {
      const r = (await apiGetJson(`/api/table-sessions?tableId=${tableId}`, tid)) as {
        session?: { id: string };
      };
      if (!r.session?.id) {
        toast.error("Abrí la mesa primero para dividir la cuenta");
        return;
      }
      window.location.href = `/app/salon/split-check?tableId=${encodeURIComponent(tableId)}&sessionId=${encodeURIComponent(r.session.id)}`;
    } catch {
      toast.error("No se pudo abrir dividir cuenta");
    }
  };

  const visibleTables = useMemo(() => {
    const base = activeSector === "all" ? tables : tables.filter((t) => t.sectorId === activeSector);
    return [...base].sort((a, b) => {
      const aAtt = a.attention?.needsAttention ? 1 : 0;
      const bAtt = b.attention?.needsAttention ? 1 : 0;
      if (aAtt !== bAtt) return bAtt - aAtt;
      const rank = (s: TableStatus) =>
        s === "PIDIENDO" ? 0 : s === "PAGO_PENDIENTE" ? 1 : s === "OCUPADA" ? 2 : 3;
      const r = rank(a.estado) - rank(b.estado);
      if (r !== 0) return r;
      return a.nombre.localeCompare(b.nombre, "es");
    });
  }, [tables, activeSector]);

  // SCRUM-145: ciclo de sector para el shortcut F3 — recorre "all" + ids de sector en orden
  const cycleSector = useCallback(() => {
    const sectorIds = ["all", ...sectors.map((s) => s.id)];
    const idx = sectorIds.indexOf(activeSector);
    setActiveSector(sectorIds[(idx + 1) % sectorIds.length]);
  }, [sectors, activeSector]);

  // Primera mesa que necesita atención en la vista actual — destino del shortcut F4
  const firstAttentionTable = useMemo(
    () => visibleTables.find((t) => t.attention?.needsAttention) ?? null,
    [visibleTables],
  );

  // SCRUM-145: shortcuts operativos del Salón — mismo patrón que KDS y POS
  const salonShortcuts = useMemo<OperationalShortcut[]>(
    () => [
      {
        id: "refresh",
        keys: "F2",
        label: "Actualizar ahora",
        description: "Recarga el estado de todas las mesas",
        handler: () => void load(),
      },
      {
        id: "cycle-sector",
        keys: "F3",
        label: "Cambiar zona",
        description: "Todos → primera zona → siguiente zona…",
        handler: cycleSector,
        disabled: sectors.length === 0,
      },
      {
        id: "first-attention",
        keys: "F4",
        label: "Ir a mesa con atención",
        description: "Abre la comanda de la primera mesa que necesita atención",
        handler: () => {
          if (!firstAttentionTable) return;
          void navigate({ to: "/app/salon/comanda", search: { tableId: firstAttentionTable.id } });
        },
        disabled: !firstAttentionTable,
      },
    ],
    [load, cycleSector, sectors.length, firstAttentionTable, navigate],
  );

  const operationalMode = useOperationalMode({
    surfaceId: "salon.floor",
    tenantId: tid,
    shortcuts: salonShortcuts,
  });

  const counts = {
    LIBRE: tables.filter((t) => t.estado === "LIBRE").length,
    OCUPADA: tables.filter((t) => t.estado === "OCUPADA").length,
    PIDIENDO: tables.filter((t) => t.estado === "PIDIENDO").length,
    PAGO_PENDIENTE: tables.filter((t) => t.estado === "PAGO_PENDIENTE").length,
  };
  const attentionCount = tables.filter((t) => t.attention?.needsAttention).length;
  const reservationByTable = useMemo(() => {
    const map = new Map<string, DayReservation>();
    for (const r of dayReservations) {
      if (r.tableId && !map.has(r.tableId)) map.set(r.tableId, r);
    }
    return map;
  }, [dayReservations]);
  const unassignedReservations = useMemo(
    () => dayReservations.filter((r) => !r.tableId),
    [dayReservations],
  );

  if (loading) {
    return (
      <div>
        <CrudShellHeader title="Salón" subtitle="Estado de mesas en tiempo real" />
        <Loading variant="list" rows={4} />
      </div>
    );
  }
  if (lock) return <FeatureLockedState payload={lock} />;

  return (
    <OperationalModeShell mode={operationalMode} className={OPERATIONAL_HOST_LAYOUT}>
      <SalonTour />
      <CrudShellHeader
        title="Salón"
        description={configMode ? "Zonas del local y mesas" : "Estado de mesas en tiempo real"}
        actions={
          <div className="flex gap-2">
            {/* En modo expandido se oculta el chrome de navegación admin — el foco es operar mesas */}
            {!operationalMode.active && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => void navigate({ to: "/app/reservas", search: {} as never })}>
                <CalendarCheck className="h-4 w-4" />
                {dayReservations.length ? `Reservas (${dayReservations.length})` : "Reservas"}
              </Button>
            )}
            {!operationalMode.active && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => void navigate({ to: "/app/salon", search: (configMode ? {} : { config: true }) as never })}>
                <Settings2 className="h-4 w-4" />
                {configMode ? "Ver salón" : "Configurar zonas y mesas"}
              </Button>
            )}
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={load}>
              <RefreshCw className="h-4 w-4" /> Actualizar
            </Button>
            <OperationalModeToggle mode={operationalMode} label="Modo salón" />
          </div>
        }
      />
      <ClinicalPageShell
        offset="none"
        maxWidth={false}
        className="!px-3 sm:!px-4 lg:!px-5 min-h-0 flex-1 space-y-3 overflow-y-auto pb-6"
      >
        <MerchantExperienceBar screenId="salon" showTour={false} />
        {configMode && tid ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Definí las zonas del local (terraza, salón, bar…) y las mesas con su capacidad.
            </p>
            <SalonFloorSettings tenantId={tid} onChanged={load} />
          </div>
        ) : null}

        {!configMode ? (
        <>
        <div className="flex flex-wrap gap-2">
          {dayReservations.length > 0 && (
            <button
              type="button"
              onClick={() => void navigate({ to: "/app/reservas", search: {} as never })}
              className="flex items-center gap-1.5 rounded-xl border border-violet-500/40 bg-violet-500/15 px-3 py-2 text-sm font-semibold text-violet-200"
            >
              <CalendarCheck className="h-4 w-4" />
              <span className="tabular-nums">{dayReservations.length}</span>
              <span>reservas hoy</span>
              {unassignedReservations.length > 0 ? (
                <span className="ml-1 text-xs font-normal text-violet-200/80">
                  · {unassignedReservations.length} sin mesa
                </span>
              ) : null}
            </button>
          )}
          {attentionCount > 0 && (
            <div className="flex items-center gap-1.5 rounded-xl border border-amber-500/40 bg-amber-500/15 px-3 py-2 text-sm font-semibold text-amber-200">
              <Bell className="h-4 w-4" />
              <span className="tabular-nums">{attentionCount}</span>
              <span>por atender</span>
            </div>
          )}
          {(Object.entries(counts) as [TableStatus, number][]).map(([status, count]) => {
            const cfg = STATUS_CONFIG[status];
            return (
              <div
                key={status}
                className={`flex items-center gap-1.5 rounded-xl border px-2.5 py-2 text-xs ${cfg.bg} ${cfg.color}`}
              >
                <span className="font-semibold tabular-nums">{count}</span>
                <span>
                  {cfg.label}
                  {count !== 1 ? "s" : ""}
                </span>
              </div>
            );
          })}
        </div>

        {sectors.length > 0 && (
          <FilterBar>
            <button
              onClick={() => setActiveSector("all")}
              className={`min-h-10 flex-shrink-0 rounded-xl border px-3 py-2 text-sm transition-colors ${activeSector === "all" ? "border-primary bg-primary/10 text-primary" : "border-border/60 text-muted-foreground hover:text-foreground"}`}
            >
              Todos
            </button>
            {sectors.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveSector(s.id)}
                className={`min-h-10 flex-shrink-0 rounded-xl border px-3 py-2 text-sm transition-colors ${activeSector === s.id ? "border-primary bg-primary/10 text-primary" : "border-border/60 text-muted-foreground hover:text-foreground"}`}
              >
                {s.nombre}
              </button>
            ))}
          </FilterBar>
        )}

        {visibleTables.length === 0 ? (
          <CrudEmptyState
            icon={UtensilsCrossed}
            title="No hay mesas configuradas"
            description="Creá zonas del local y mesas desde Configurar."
            action={
              <Button
                onClick={() => void navigate({ to: "/app/salon", search: { config: true } })}
              >
                <Settings2 className="h-4 w-4" /> Configurar zonas y mesas
              </Button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {visibleTables.map((table) => (
              <TableCard
                key={table.id}
                table={table}
                tid={tid}
                reservation={reservationByTable.get(table.id) ?? null}
                expanded={operationalMode.active}
                onOpen={handleOpen}
                onClose={handleClose}
                onSplit={handleSplit}
                onReservationClick={setSelectedReservation}
              />
            ))}
          </div>
        )}

        {unassignedReservations.length > 0 ? (
          <div className="space-y-2 rounded-xl border border-violet-500/30 bg-violet-500/10 p-3">
            <p className="text-xs font-semibold text-violet-200">Reservas sin mesa asignada</p>
            <div className="flex flex-wrap gap-2">
              {unassignedReservations.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setSelectedReservation(r)}
                  className="rounded-lg border border-violet-500/35 bg-card/60 px-3 py-2 text-left text-xs text-violet-100 transition-colors hover:border-violet-400/50"
                >
                  <span className="font-semibold">{r.reservedTime.slice(0, 5)}</span>
                  {" · "}
                  {r.guestName}
                  {" · "}
                  {r.partySize} pers.
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <SalonReservationSheet
          open={Boolean(selectedReservation)}
          onOpenChange={(open) => {
            if (!open) setSelectedReservation(null);
          }}
          reservation={selectedReservation}
          tenantId={tid}
          canOpenTable={Boolean(
            selectedReservation?.tableId &&
              tables.find((x) => x.id === selectedReservation.tableId)?.estado === "LIBRE",
          )}
          onChanged={load}
          onSeatAndOpen={async (tableId, partySize) => {
            handleOpen(tableId, partySize);
          }}
        />

        <OpenTableDialog
          open={Boolean(openTarget)}
          defaultComensales={openTarget?.defaultComensales ?? 2}
          onOpenChange={(open) => {
            if (!open) setOpenTarget(null);
          }}
          onConfirm={(comensales) => void confirmOpenTable(comensales)}
        />
        </>
        ) : null}
      </ClinicalPageShell>
    </OperationalModeShell>
  );
}
